import React, { useState } from 'react';
import MainNavigation from '../../components/ui/MainNavigation';
import FooterSection from '../homepage/components/FooterSection';
import FeaturedBrandSection from '../../components/FeaturedBrandSection';
import ComparisonModal from '../../components/ComparisonModal';
import HeroSection from './components/HeroSection';
import WhyBatterySection from './components/WhyBatterySection';
import BatteryTypesSection from './components/BatteryTypesSection';
import SystemIntegrationSection from './components/SystemIntegrationSection';
import CostsSubsidiesSection from './components/CostsSubsidiesSection';
import TechnicalSpecsSection from './components/TechnicalSpecsSection';
import ProvidersReviewsSection from './components/ProvidersReviewsSection';
import GrpGarantieVergelijking from '../../components/GrpGarantieVergelijking';
import CalculatorPage from '../calculator';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../components/AppIcon';

const BatteryStorageCategoryPage = () => {
  const [comparisonProducts, setComparisonProducts] = useState([]);
  const [isComparisonOpen, setIsComparisonOpen] = useState(false);

  const handleAddToCompare = (product) => {
    setComparisonProducts((prev) => {
      const exists = prev?.find(p => p?.id === product?.id);
      if (exists) {
        return prev;
      }
      if (prev?.length >= 3) {
        return [...prev?.slice(1), product];
      }
      return [...prev, product];
    });
  };

  const handleRemoveFromCompare = (productId) => {
    setComparisonProducts((prev) => prev?.filter(p => p?.id !== productId));
  };

  const handleClearComparison = () => {
    setComparisonProducts([]);
  };

  const featuredBrandData = {
    brandName: "GRP",
    brandLogo: "https://ui-avatars.com/api/?name=GRP&background=16a34a&color=fff&size=80",
    brandDescription: "Innovatieve grafeen technologie voor duurzame energie-opslag",
    productName: "GRP Wall Batterij",
    productImage: "/assets/images/GRP-Wall-1783594197902.png",
    productImageAlt: "GRP Wall thuisbatterij met geavanceerde grafeen technologie en modern design",
    productDescription: "Revolutionair batterijsysteem met grafeen technologie. Met 20.000 cycli en uitzonderlijke levensduur de meest duurzame keuze voor uw woning.",
    productSpecs: [
      { icon: "Battery", label: "Levensduur", value: "20.000 cycli" },
      { icon: "Zap", label: "Technologie", value: "Grafeen" },
      { icon: "Shield", label: "Garantie", value: "15 jaar" },
      { icon: "TrendingUp", label: "Efficiëntie", value: "95%" }
    ],
    productPrice: "€8.900",
    rating: 4.9,
    reviewCount: 287,
    reviews: [
      {
        rating: 5,
        text: "De GRP Wall overtreft alle verwachtingen. De grafeen technologie zorgt voor sneller laden en langere levensduur. Na 6 maanden nog steeds 100% tevreden!",
        name: "Linda Bakker",
        location: "Eindhoven",
        avatar: "https://ui-avatars.com/api/?name=Linda+B&background=16a34a&color=fff",
        verified: true
      },
      {
        rating: 5,
        text: "GRP heeft echt iets unieks. De 20.000 cycli garantie geeft enorm veel vertrouwen. Installatie was professioneel en snel geregeld.",
        name: "Robert Hendriks",
        location: "Den Haag",
        avatar: "https://ui-avatars.com/api/?name=Robert+H&background=15803d&color=fff",
        verified: true
      },
      {
        rating: 5,
        text: "Beste investering voor ons huis. De GRP grafeen technologie maakt echt verschil in prestaties. Zeer innovatief en betrouwbaar systeem.",
        name: "Emma de Jong",
        location: "Groningen",
        avatar: "https://ui-avatars.com/api/?name=Emma+dJ&background=166534&color=fff",
        verified: true
      }
    ],
    colorScheme: "from-green-500/10 to-emerald-500/10",
    accentColor: "text-green-600",
    bgGradient: "from-green-50 via-emerald-50 to-teal-100"
  };

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />
      {/* Main Content with proper spacing for fixed navigation */}
      <main className="pt-16 lg:pt-0 lg:ml-[72px] transition-all duration-300">
      <FeaturedBrandSection {...featuredBrandData} onAddToCompare={handleAddToCompare} />
      <HeroSection />
      <WhyBatterySection />
      <BatteryTypesSection />
      <SystemIntegrationSection />
      <CostsSubsidiesSection />
      <TechnicalSpecsSection />
      <GrpGarantieVergelijking
        ctaHref="/quote-request-form"
        ctaTekst="Plan een gratis adviesgesprek"
        onCtaClick={() => { window.location.href = "/quote-request-form"; }}
      />
      <section id="rekenhulp" className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground leading-tight">
              Wat levert een thuisbatterij jou op na 2027?
            </h2>
            <p className="mt-4 font-body text-muted-foreground">
              Vijf vragen, twee minuten. Je ziet direct je besparing, de terugverdientijd en welke batterij bij je past.
            </p>
          </div>
          <CalculatorPage embedded />
        </div>
      </section>
      <ProvidersReviewsSection onAddToCompare={handleAddToCompare} />
      </main>
      <FooterSection />
      {/* Floating Comparison Button */}
      <AnimatePresence>
        {comparisonProducts?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="fixed bottom-8 right-8 z-40"
          >
            <motion.button
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsComparisonOpen(true)}
              className="relative bg-gradient-to-r from-primary to-primary/80 text-white px-6 py-4 rounded-2xl shadow-2xl hover:shadow-primary/50 transition-all duration-300 flex items-center space-x-3"
            >
              <motion.div
                animate={{
                  rotate: [0, 10, -10, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <Icon name="GitCompare" size={24} />
              </motion.div>
              <div className="text-left">
                <p className="text-sm font-semibold">Vergelijk Producten</p>
                <p className="text-xs opacity-90">{comparisonProducts?.length} geselecteerd</p>
              </div>
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="absolute -top-2 -right-2 bg-white text-primary rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm shadow-lg"
              >
                {comparisonProducts?.length}
              </motion.div>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Comparison Modal */}
      <ComparisonModal
        isOpen={isComparisonOpen}
        onClose={() => setIsComparisonOpen(false)}
        products={comparisonProducts}
        category="thuisbatterij"
      />
    </div>
  );
};

export default BatteryStorageCategoryPage;